/**
 * VistoeraTASK-00229 — Ambiguous Upload Outcome Reconciliation.
 *
 * FORENSIC ROOT CAUSE (see the task's final report for full detail):
 * `api-preview.vistoera.com` is proxied through Cloudflare in "Full
 * (strict)" mode (`docs/deployment/ONLINE_PREVIEW_ARCHITECTURE.md`).
 * Cloudflare's own edge-to-origin connection has a timeout budget well
 * under this app's deliberately generous client-side axios timeout
 * (180_000ms for `uploadSiteAsset`, see `api.ts`). A large-but-legal
 * image on a slow/typical upload link can exceed Cloudflare's edge
 * budget while the FastAPI origin (Render) is still happily receiving
 * the request body and about to finish `asset_runtime_service.
 * create_asset` successfully — the browser then sees a bare network
 * error / gateway-style response with NO parseable business body long
 * before the client's own 180s timeout would ever fire. The origin
 * request very likely still completes and a real `SiteAsset` row gets
 * created; the client just never learns about it.
 *
 * This is EXACTLY the "server may have succeeded, client is uncertain"
 * case the TASK-00220/00221 canonical state machine calls out (spec §16
 * "Ambiguous Timeout" — never FAILED, never a silent auto-repost). This
 * module implements the safest reconciliation available WITHOUT any new
 * backend endpoint/column/migration (per TASK-00221 §三 "不要立即新增 DB
 * 字段" and TASK-00229 §三 "先取证，不要先调大 timeout"): the existing
 * `listSiteAssets` (`GET /workspaces/{id}/assets?site_id=...`) already
 * returns each asset's `checksum`/`original_filename`/`created_at` —
 * enough to recognize "the asset my own just-failed request most likely
 * created" without touching `create_asset`'s dedup behavior at all (this
 * is a pure READ-time reconciliation, never a write-time skip — it can
 * NEVER suppress a legitimate new Asset row, so it cannot violate the
 * "identical-content re-upload is legal, produces an independent Asset"
 * contract `test_create_asset_duplicate_bytes_are_safe` locks in).
 *
 * Deliberately NOT wired into a global "reject duplicate checksum"
 * anything — `findReconciledAssetMatch` is only ever invoked from the
 * catch-block of ONE specific upload attempt, for ONE specific node/
 * File, immediately after THAT attempt's own ambiguous transport error,
 * and only within a short recency window. It answers "did MY own
 * request that I just lost the response to actually succeed?", never
 * "does an asset with this content already exist anywhere, ever".
 */

/** Cloudflare/edge/network-level outcomes where we genuinely do not know
 * whether the origin finished the request — as opposed to a definite
 * backend business rejection (4xx with a real structured error body,
 * e.g. 413 "file too large", 422 validation, 404 site not found). Only
 * the former is worth spending a reconciliation round-trip on; a
 * definite 4xx should surface as FAILED immediately (spec §九 — an
 * oversize/invalid file must fail fast, not sit in "confirming...").
 *
 * `err.status` here is `SaasApiError.status` (`api.ts`'s response
 * interceptor) — `undefined` for a true no-response transport failure
 * (client timeout, offline, CORS-masked failure, or Cloudflare killing
 * the connection before ANY HTTP response reaches the browser), and any
 * numeric HTTP status when one **was** received. 5xx/52x-range statuses
 * (500/502/503/504, and Cloudflare's own 520-527 edge error codes when
 * they DO reach the browser as a real response) are treated as
 * ambiguous too — none of them carry a structured business rejection,
 * and several (502/504/522/524) are textbook proxy/gateway-timeout
 * signatures, not a deliberate application decision. */
export function isAmbiguousUploadFailure(err: unknown): boolean {
  const status = (err as { status?: number } | null | undefined)?.status
  if (status === undefined || status === null) return true
  return status >= 500
}

function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let hex = ''
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0')
  }
  return hex
}

/** SHA-256 hex digest of a File's bytes, via the standard Web Crypto API
 * (available on every origin this app runs on — HTTPS Preview/Production,
 * and `localhost` for local dev). Matches the backend's own
 * `hashlib.sha256(...).hexdigest()` (`asset_runtime_service.
 * _checksum_bytes_of_file`) byte-for-byte, so a match here is a genuine
 * content match, not a heuristic. Only ever called from the ambiguous-
 * failure reconciliation path (never on the happy path) — for a file
 * already within `IMAGE_MAX_FILE_SIZE` (20MB), `crypto.subtle.digest` on
 * an already-in-memory `ArrayBuffer` completes in well under a second on
 * any real device, no meaningful added cost (spec §二十二). */
export async function computeFileSha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return bufferToHex(digest)
}

export interface ReconciliationCandidate {
  id: string
  checksum?: string | null
  original_filename?: string | null
  created_at?: string | null
}

/** How far back to look for "the asset my own request probably just
 * created". Generous enough to cover a genuinely slow large-file
 * transfer plus a few seconds of reconciliation round-trip latency,
 * tight enough that it will essentially never accidentally bind to an
 * unrelated identical-content asset from a much earlier, deliberate
 * re-import (which — per the TASK-00221 contract — must remain its own
 * independent Asset, never silently reused). 15 minutes covers even a
 * very large image on a very slow uplink while staying well short of
 * "the user could plausibly have walked away and come back to
 * deliberately re-drop the same file". */
export const RECONCILIATION_LOOKBACK_MS = 15 * 60 * 1000

/** VistoeraTASK-00265/00266: Production HAR showed parallel large POSTs to
 * api.vistoera.com returning Cloudflare 502 near ~32s while smaller files
 * in the same batch succeeded. */
export const CANVAS_IMAGE_HEAVY_UPLOAD_BYTES = 4 * 1024 * 1024

/** VistoeraTASK-00407 — stability-first default for Canvas ingestion
 * (one drop may carry N files, but uploads run strictly serially). */
export const DEFAULT_CANVAS_IMAGE_UPLOAD_CONCURRENCY = 1

/** Legacy adaptive policy (TASK-00265 forensic) — kept for tests/docs;
 * not used by Canvas OS-drop scheduling while DEFAULT=1. */
export function resolveLegacyAdaptiveOsFileDropImageUploadConcurrency(files: File[]): number {
  const count = Math.max(1, files.length)
  const maxBytes = files.reduce((m, f) => Math.max(m, f.size), 0)
  if (maxBytes >= 8 * 1024 * 1024) return 1
  if (maxBytes >= CANVAS_IMAGE_HEAVY_UPLOAD_BYTES) return Math.min(2, count)
  return Math.min(4, count)
}

/** Bounded concurrency for Canvas OS image drops. */
export function resolveOsFileDropImageUploadConcurrency(_files: File[]): number {
  return DEFAULT_CANVAS_IMAGE_UPLOAD_CONCURRENCY
}

/** listSiteAssets reconciliation with backoff after ambiguous upload failures. */
const RECONCILE_LIST_RETRY_DELAYS_MS = [0, 1500, 4000, 8000, 15000]

export async function attemptSiteAssetUploadReconciliation(
  file: File,
  listSiteAssets: () => Promise<{ data: { items: ReconciliationCandidate[] } }>,
  lookbackMs: number = RECONCILIATION_LOOKBACK_MS,
): Promise<ReconciliationCandidate | null> {
  const checksum = await computeFileSha256Hex(file)
  const nowMs = Date.now()
  for (const delay of RECONCILE_LIST_RETRY_DELAYS_MS) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
    try {
      const { data } = await listSiteAssets()
      const match = findReconciledAssetMatch(data.items, checksum, file.name, nowMs, lookbackMs)
      if (match) return match
    } catch (err) {
      if (!isAmbiguousUploadFailure(err)) return null
    }
  }
  return null
}

/** Finds the most likely match for "the asset this specific upload
 * attempt just created, whose response I never received" — checksum is
 * the authoritative signal (byte-for-byte content match), filename is a
 * secondary corroborating signal (guards against the vanishingly rare
 * SHA-256 collision), and the recency window bounds it to plausibly-this-
 * request. Returns the newest match if (pathologically) more than one
 * candidate qualifies. Never mutates/dedupes/deletes anything — purely a
 * lookup. */
export function findReconciledAssetMatch(
  candidates: ReconciliationCandidate[],
  checksum: string,
  filename: string,
  nowMs: number,
  lookbackMs: number = RECONCILIATION_LOOKBACK_MS,
): ReconciliationCandidate | null {
  const cutoff = nowMs - lookbackMs
  let best: ReconciliationCandidate | null = null
  let bestTime = -Infinity
  for (const c of candidates) {
    if (!c.checksum || c.checksum !== checksum) continue
    if ((c.original_filename || '') !== filename) continue
    const t = c.created_at ? Date.parse(c.created_at) : NaN
    if (Number.isNaN(t) || t < cutoff) continue
    if (t > bestTime) {
      best = c
      bestTime = t
    }
  }
  return best
}

// ==========================================================
// VistoeraTASK-00232 — Audio/Video extension.
// ==========================================================
//
// AUDIO forensic finding: `upload_audio_asset`
// (`audio_asset_library_service.py`) writes a canonical
// `SiteAsset(media_type="audio")` row — the EXACT SAME table image
// uploads use — and already computes+stores a real
// `hashlib.sha256(file_bytes).hexdigest()` checksum on it (existing
// behavior, not new). `list_assets_for_site` (backing `listSiteAssets`)
// filters ONLY by `site_id`, never by `media_type` — so it already
// returns audio rows too. This means `isAmbiguousUploadFailure` +
// `computeFileSha256Hex` + `findReconciledAssetMatch` above are reused
// COMPLETELY UNCHANGED for audio; only the lookback window differs (see
// `AUDIO_RECONCILIATION_LOOKBACK_MS` below) — no new function needed.
//
// VIDEO forensic finding: video OS-drop uploads
// (`video_generation_assets.upload_direct`) write to a DIFFERENT table
// (`VideoGenerationAsset`, via `/workspaces/{id}/video-assets/upload`)
// that has NO checksum column at all today, and this codebase has no
// Alembic/versioned-migration tooling (`grep`-confirmed zero hits for
// ADD COLUMN/alembic anywhere in `backend/`) — adding one here would be
// exactly the kind of "贸然" schema change spec §十九 forbids for this
// task. `listVideoAssets`'s upload-sourced items DO already carry a
// reliable, non-invented identity triple instead: `filename` (=
// `asset.original_filename`), `size_bytes` (= the exact byte count the
// server actually wrote, from `upload_direct`'s streaming loop), and
// `created_at` (= `asset.completed_at`). Matching on all three (never
// filename alone, spec §十六) is the strongest signal available without
// a backend change — and since VIDEO has no server checksum to compare
// against anyway, paying the ~300MB client-side `file.arrayBuffer()`
// cost to hash a video would buy nothing (see `VIDEO_CHECKSUM_PEAK_
// MEMORY_ESTIMATE` in the final report) — so video deliberately never
// calls `computeFileSha256Hex` at all, on any path.

/** 35 minutes — `uploadAudioAsset`'s own real client timeout is
 * 1_200_000ms (20min, TASK-00220/00221, derived from the real 150MB
 * backend cap at the same throughput floor `uploadSiteAsset` uses). The
 * window just needs to safely outlast "how long ago could the actual
 * HTTP request that hit this ambiguous error have started" (bounded by
 * that same 20min client timeout) plus buffer for reconcile-call latency
 * and clock skew — not a proportional multiple of it. */
export const AUDIO_RECONCILIATION_LOOKBACK_MS = 35 * 60 * 1000

/** 60 minutes — same reasoning, against `uploadVideoAsset`'s real
 * 2_400_000ms (40min) client timeout. */
export const VIDEO_RECONCILIATION_LOOKBACK_MS = 60 * 60 * 1000

export interface SizeReconciliationCandidate {
  id: string
  size_bytes?: number | null
  filename?: string | null
  created_at?: string | null
}

/** VIDEO-only fallback matcher (no checksum available server-side — see
 * module doc above). Requires an EXACT filename match AND an EXACT
 * byte-size match AND recency — never filename alone (spec §十六
 * "禁止用文件名 alone 作为成功匹配依据"). Two independently-uploaded,
 * differently-named-or-sized videos cannot collide; two DIFFERENT
 * videos that happen to share both an identical filename AND an
 * identical exact byte count AND land within the same recency window is
 * the only theoretical collision, which is the same order of
 * (vanishingly small, and inherently no-worse-than-before-this-task)
 * risk class as any filename+size heuristic anywhere else in this
 * codebase — not a new weakness this task introduces. */
export function findReconciledAssetMatchBySize(
  candidates: SizeReconciliationCandidate[],
  filename: string,
  sizeBytes: number,
  nowMs: number,
  lookbackMs: number = VIDEO_RECONCILIATION_LOOKBACK_MS,
): SizeReconciliationCandidate | null {
  const cutoff = nowMs - lookbackMs
  let best: SizeReconciliationCandidate | null = null
  let bestTime = -Infinity
  for (const c of candidates) {
    if ((c.filename || '') !== filename) continue
    if (c.size_bytes === undefined || c.size_bytes === null || c.size_bytes !== sizeBytes) continue
    const t = c.created_at ? Date.parse(c.created_at) : NaN
    if (Number.isNaN(t) || t < cutoff) continue
    if (t > bestTime) {
      best = c
      bestTime = t
    }
  }
  return best
}
